import { COUNTRY_CODES, compareLocalDate, nationalityName } from '@felix/domain';
import { emptySignOnForm, type SignOnForm, signOnBody, signOnFieldsReady } from './sign-on-off.js';
import type {
  CrewAssignment,
  CrewCardUpdate,
  CrewMedicalInput,
  CrewMedicalRecord,
  CrewProfileInput,
  CrewProfileView,
  NewCrewCardInput,
} from './types.js';

/**
 * Personel kartı penceresi ve Crew Profile ekranının saf modeli (ADR 0131 §1, §2, Ek 1). Doğrulama
 * sunucuda (`@felix/domain` `parseCrewProfile`); burada yalnız gönderme koşulu ve gövde.
 */

/** Pencerenin alanları; hepsi metin (boş = yok). */
export interface CrewCardForm {
  fullName: string;
  crewCode: string;
  nationality: string;
  dateOfBirth: string;
  placeOfBirth: string;
  gender: string;
  maritalStatus: string;
  phone: string;
  contactEmail: string;
  address: string;
  emergencyName: string;
  emergencyRelation: string;
  emergencyPhone: string;
  /** Kıyafet ve fiziksel özellikler (ADR 0158): yalnız ofiste. */
  coverallSize: string;
  upperSize: string;
  lowerSize: string;
  shoeSize: string;
  heightCm: string;
  weightKg: string;
  eyeColor: string;
  hairColor: string;
  distinguishingMarks: string;
  /** "Create login": giriş hesabı da açılsın (yalnız yeni kartta). */
  createLogin: boolean;
  loginEmail: string;
  /** İşlemi yapanın parolası (hesap açmanın onayı; ADR 0042 §23). */
  password: string;
  /**
   * "Sign on now": kartla birlikte katılış (yalnız yeni kartta, Sign On yetkisiyle; ADR 0131 Ek 4
   * §2). Kişi alanı (`userId`) kullanılmaz: kişi bu kart.
   */
  signOnNow: boolean;
  signOn: SignOnForm;
}

const PROFILE_KEYS = [
  'crewCode',
  'nationality',
  'dateOfBirth',
  'placeOfBirth',
  'gender',
  'maritalStatus',
  'phone',
  'contactEmail',
  'address',
  'emergencyName',
  'emergencyRelation',
  'emergencyPhone',
  'coverallSize',
  'upperSize',
  'lowerSize',
  'shoeSize',
  'heightCm',
  'weightKg',
  'eyeColor',
  'hairColor',
  'distinguishingMarks',
] as const satisfies readonly (keyof CrewProfileInput & keyof CrewCardForm)[];

export function emptyCrewCardForm(): CrewCardForm {
  return {
    fullName: '',
    crewCode: '',
    nationality: '',
    dateOfBirth: '',
    placeOfBirth: '',
    gender: '',
    maritalStatus: '',
    phone: '',
    contactEmail: '',
    address: '',
    emergencyName: '',
    emergencyRelation: '',
    emergencyPhone: '',
    coverallSize: '',
    upperSize: '',
    lowerSize: '',
    shoeSize: '',
    heightCm: '',
    weightKg: '',
    eyeColor: '',
    hairColor: '',
    distinguishingMarks: '',
    createLogin: false,
    loginEmail: '',
    password: '',
    signOnNow: false,
    signOn: emptySignOnForm(),
  };
}

/** Düzenleme penceresinin ilk değerleri: kartın alanları. */
export function formOfProfile(view: CrewProfileView): CrewCardForm {
  const form = { ...emptyCrewCardForm(), fullName: view.fullName };
  for (const key of PROFILE_KEYS) form[key] = view.profile?.[key] ?? '';
  return form;
}

export function crewCardReady(form: CrewCardForm): boolean {
  if (form.fullName.trim() === '') return false;
  // Sign On kuralı: kartta uyruk ve doğum tarihi zorunlu (ADR 0131 §3; FAL 5 ve yaş denetimi)
  if (form.signOnNow) {
    if (form.nationality === '' || form.dateOfBirth === '') return false;
    if (!signOnFieldsReady(form.signOn)) return false;
  }
  if (!form.createLogin) return true;
  return form.loginEmail.trim() !== '' && form.password !== '';
}

function profileOf(form: CrewCardForm): CrewProfileInput {
  const profile: CrewProfileInput = {};
  for (const key of PROFILE_KEYS) {
    const value = form[key].trim();
    profile[key] = value === '' ? null : value;
  }
  return profile;
}

/**
 * "+ Add Crew" gövdesi; hesap istenmediyse giriş alanları, "Sign on now" kapalıysa katılış
 * gönderilmez.
 */
export function newCrewCardInput(form: CrewCardForm): NewCrewCardInput {
  return {
    fullName: form.fullName.trim(),
    profile: profileOf(form),
    ...(form.createLogin
      ? { login: { email: form.loginEmail.trim() }, password: form.password }
      : {}),
    ...(form.signOnNow ? { signOn: signOnBody(form.signOn) } : {}),
  };
}

/** Edit gövdesi: kartın sürümüyle; ad yalnız değiştiyse (kişinin sürümüyle). */
export function crewCardUpdate(form: CrewCardForm, view: CrewProfileView): CrewCardUpdate {
  const fullName = form.fullName.trim();
  return {
    ...(fullName === view.fullName ? {} : { fullName, userRowVersion: view.rowVersion }),
    profile: profileOf(form),
    rowVersion: view.profile?.rowVersion ?? null,
  };
}

/**
 * "Clothing & physical" sekmesinin alanları ve önerilen bedenler (ADR 0158): beden serbest
 * metindir (ör. `XL`, `52`); seçenekler yalnız öneridir.
 */
export const PHYSICAL_KEYS = [
  'coverallSize',
  'upperSize',
  'lowerSize',
  'shoeSize',
  'heightCm',
  'weightKg',
  'eyeColor',
  'hairColor',
  'distinguishingMarks',
] as const satisfies readonly (keyof CrewCardForm)[];

export const CLOTHING_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'] as const;

/** Sağlık sekmesinin alanları (ADR 0158 §2); hepsi metin (boş = yok). */
export interface CrewMedicalForm {
  bloodGroup: string;
  medicalHistory: string;
  medications: string;
  allergies: string;
}

const MEDICAL_KEYS = ['bloodGroup', 'medicalHistory', 'medications', 'allergies'] as const;

export function medicalFormOf(record: CrewMedicalRecord | null): CrewMedicalForm {
  return {
    bloodGroup: record?.bloodGroup ?? '',
    medicalHistory: record?.medicalHistory ?? '',
    medications: record?.medications ?? '',
    allergies: record?.allergies ?? '',
  };
}

/** Sağlık gövdesi: boş alan `null`; kayıt yoksa sürüm `null` (ilk kayıt). */
export function medicalInput(
  form: CrewMedicalForm,
  record: CrewMedicalRecord | null,
): { medical: CrewMedicalInput; rowVersion: number | null } {
  const medical: CrewMedicalInput = {};
  for (const key of MEDICAL_KEYS) {
    const value = form[key].trim();
    medical[key] = value === '' ? null : value;
  }
  return { medical, rowVersion: record?.rowVersion ?? null };
}

/** Form kayıtlı değerden farklı mı (Kaydet düğmesi). */
export function medicalChanged(form: CrewMedicalForm, record: CrewMedicalRecord | null): boolean {
  const saved = medicalFormOf(record);
  return MEDICAL_KEYS.some((key) => form[key].trim() !== saved[key]);
}

/** Uyruk seçici: ISO 3166-1 alpha-2 kodları, dile göre adla sıralı. */
export function nationalityOptions(locale: string): { value: string; label: string }[] {
  return COUNTRY_CODES.map((code) => ({ value: code, label: nationalityName(code, locale) })).sort(
    (x, y) => x.label.localeCompare(y.label, locale),
  );
}

export type ProfileTab = 'overview' | 'contracts' | 'joining' | 'notes' | 'documents' | 'training';

/**
 * Crew Profile sekmeleri (`gemi-personel.jpeg` panel 3): ilk üçü sayfa içinde; Documents ve
 * Training kendi ekranlarına kişiyle açılır (`?user=`). CV, Leave & Rotation sonraki fazlarda.
 */
export function profileTabs(
  options: { notes?: boolean } = {},
): { id: ProfileTab; link: string | null }[] {
  return [
    { id: 'overview', link: null },
    { id: 'contracts', link: null },
    { id: 'joining', link: null },
    // Office notes (ADR 0159): yalnız web ofiste ve `crew_notes.view` ile; gemide hiç yok
    ...(options.notes ? [{ id: 'notes' as const, link: null }] : []),
    { id: 'documents', link: '/crew/certificates' },
    { id: 'training', link: '/crew/training' },
  ];
}

export type AssignmentState = 'planned' | 'active' | 'completed';

/** Atamanın bugünkü durumu (gemi/ofis günü; bitiş günü dahil). */
export function assignmentState(
  assignment: Pick<CrewAssignment, 'validFrom' | 'validTo'>,
  today: string,
): AssignmentState {
  if (compareLocalDate(assignment.validFrom, today) > 0) return 'planned';
  if (assignment.validTo !== null && compareLocalDate(assignment.validTo, today) < 0) {
    return 'completed';
  }
  return 'active';
}

/** Sunucu hata kodunun metni (`crewProfile.errors.*`). */
export function crewCardErrorKey(code: string): string {
  return `crewProfile.errors.${code}`;
}
